<script lang="ts">
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { formatDailyAverageScore, formatDistance, getMode, CONTINENT_BY_SLUG } from '@atlasguess/games';
  import { DAILY_ROUND_COUNT } from '../../lib/constants';
  import { api, ApiError } from '../../lib/api';
  import Card from '../../components/ui/Card.svelte';
  import Spinner from '../../components/ui/Spinner.svelte';
  import EmptyState from '../../components/ui/EmptyState.svelte';
  import Button from '../../components/ui/Button.svelte';
  import DonorIcon from '../../components/DonorIcon.svelte';
  import FollowButton from '../../components/FollowButton.svelte';
  import FollowsList from '../../components/FollowsList.svelte';
  import RecentDailiesTable from '../../components/RecentDailiesTable.svelte';
  import { type DonorTier } from '../../lib/stores/auth';
  import { type FollowToggleResponse } from '../../lib/stores/following';
  import { withPageTitle } from '../../lib/page-title';
  import { fetchPublicStats, formatQuizBest, type PublicStats } from '../../lib/stats';
  import { displayUnit } from '../../lib/units';

  type Tab = 'stats' | 'following' | 'followers';

  const TABS: { id: Tab; label: string }[] = [
    { id: 'stats', label: 'Stats' },
    { id: 'following', label: 'Following' },
    { id: 'followers', label: 'Followers' },
  ];

  function readInitialTab(): Tab {
    if (typeof window === 'undefined') return 'stats';
    const slug = new URL(window.location.href).searchParams.get('tab');
    return TABS.some((t) => t.id === slug) ? (slug as Tab) : 'stats';
  }

  let activeTab = $state<Tab>(readInitialTab());

  // Mirror tab to ?tab= so refreshes / shared links land on the same
  // panel. Default 'stats' is represented as a missing param to keep
  // canonical /u/{username} URLs clean.
  $effect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (activeTab === 'stats') {
      url.searchParams.delete('tab');
    } else {
      url.searchParams.set('tab', activeTab);
    }
    window.history.replaceState({}, '', url.toString());
  });

  // Public-facing profile page. No auth required. The server returns
  // 404 for unknown users; anonymized accounts (post-7-day) keep their
  // username so leaderboards still resolve and the page shows them as
  // "Deleted user" — but stats stay null in that case too.

  interface PublicProfile {
    username: string;
    donor_tier: DonorTier;
    member_since: string;
    followers_count: number;
    following_count: number;
    is_following: boolean;
    stats: null;
  }

  interface Props {
    username: string;
  }

  let { username }: Props = $props();

  let profile = $state<PublicProfile | null>(null);
  let stats = $state<PublicStats | null>(null);
  let notFound = $state(false);
  let loading = $state(true);

  const formatKm = (km: number | null): string =>
    km === null ? '—' : formatDistance(km, $displayUnit);

  // Same filter-slug → label mapping the account stats tab uses, kept
  // local to this component for symmetry with the other format-card
  // helpers (rather than extracting a shared util for two callers).
  function filterLabel(slug: string): string {
    if (slug === '') return 'All';
    if (CONTINENT_BY_SLUG[slug]) return CONTINENT_BY_SLUG[slug];
    return slug
      .split('-')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
  }

  // Mode-id → display label. Uses the registered mode's displayName
  // when available, falls back to a tidied slug otherwise.
  function modeLabel(modeId: string): string {
    const mode = getMode(modeId);
    if (mode) return mode.displayName;
    return modeId
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  // Continent-only label for the Quiz card per-mode list — the
  // surrounding card already says "Quiz" so the framing words are
  // redundant. See StatsTab for the same helper.
  function quizModeLabel(modeId: string): string {
    const m = modeId.match(/^countries-(.+)-quiz$/);
    if (m) {
      const slug = m[1];
      if (CONTINENT_BY_SLUG[slug]) return CONTINENT_BY_SLUG[slug];
      return slug
        .split('-')
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ');
    }
    return modeLabel(modeId);
  }

  // FollowButton notifies us with the server's authoritative counts after
  // a successful toggle. We update local state in place so the displayed
  // followers / following numbers tick immediately, no profile refetch.
  function handleFollowChange(res: FollowToggleResponse): void {
    if (!profile) return;
    profile = {
      ...profile,
      is_following: res.is_following,
      followers_count: res.followers_count,
      following_count: res.following_count,
    };
  }

  // Title shows the username (canonicalized lowercase) so the browser-tab
  // label is identifiable. We track the username prop, not profile.username,
  // so the title is correct during the first paint before the API resolves.
  $effect(() => withPageTitle(`@${username.toLowerCase()}`));

  onMount(async () => {
    // Case-fold mixed-case URLs to canonical lowercase. Server-side 301
    // isn't possible on Cloudflare Pages, so this lives in the SPA.
    const lower = username.toLowerCase();
    if (lower !== username) {
      navigate(`/u/${lower}`, { replace: true });
      return;
    }

    try {
      profile = await api<PublicProfile>(`/api/users/${encodeURIComponent(lower)}`);
      // Stats are a separate endpoint so a fresh user with no rounds
      // still gets a fast profile load — and a profile-only failure
      // doesn't blank the whole page if the stats query 5xx's.
      try {
        stats = await fetchPublicStats(lower);
      } catch {
        stats = null;
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        notFound = true;
      }
    } finally {
      loading = false;
    }
  });
</script>

{#if loading}
  <div class="page-center"><Spinner size="lg" /></div>
{:else if notFound}
  <EmptyState
    title="No such player"
    body="The username you tried doesn't exist on AtlasGuess."
  >
    {#snippet action()}
      <Button variant="primary" href="/">Back to home</Button>
    {/snippet}
  </EmptyState>
{:else if profile}
  <Card padding="lg" elevated>
    <div class="profile">
      <header class="profile__header">
        <div class="profile__identity">
          <DonorIcon tier={profile.donor_tier} size={36} />
          <h2 class="profile__username">{profile.username}</h2>
        </div>
        <FollowButton
          username={profile.username}
          size="lg"
          onchange={handleFollowChange}
        />
      </header>
      <dl class="profile__facts">
        <div>
          <dt>Member since</dt>
          <dd>{new Date(profile.member_since).toLocaleDateString()}</dd>
        </div>
        <div>
          <dt>Followers</dt>
          <dd>{profile.followers_count.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Following</dt>
          <dd>{profile.following_count.toLocaleString()}</dd>
        </div>
      </dl>

      <div class="profile__tabs" role="tablist" aria-label="Profile sections">
        {#each TABS as tab}
          <button
            type="button"
            role="tab"
            id="profile-tab-{tab.id}"
            aria-controls="profile-panel-{tab.id}"
            aria-selected={activeTab === tab.id}
            class="profile__tab"
            class:profile__tab--active={activeTab === tab.id}
            onclick={() => (activeTab = tab.id)}
          >
            {tab.label}
          </button>
        {/each}
      </div>

      <div
        class="profile__panel"
        role="tabpanel"
        id="profile-panel-{activeTab}"
        aria-labelledby="profile-tab-{activeTab}"
      >
        {#if activeTab === 'stats'}
          <section class="profile__section">
            {#if stats && stats.totals.rounds_played > 0}
              <div class="stats">
                <div class="stats__totals">
                  <div class="stats__tile">
                    <div class="stats__tile-value">{stats.totals.rounds_played.toLocaleString()}</div>
                    <div class="stats__tile-label">Rounds played</div>
                  </div>
                  <div class="stats__tile">
                    <div class="stats__tile-value">{stats.totals.modes_completed}</div>
                    <div class="stats__tile-label">Modes played</div>
                  </div>
                </div>
                <div class="stats__format-grid">
                  <div class="stats__format-card">
                    <h4>Daily</h4>
                    <dl>
                      <div><dt>Runs</dt><dd>{stats.by_format.daily.runs}</dd></div>
                      <div>
                        <dt>Best score</dt>
                        <dd>{formatDailyAverageScore(stats.by_format.daily.best_total_score, DAILY_ROUND_COUNT)}</dd>
                      </div>
                    </dl>
                  </div>
                  <div class="stats__format-card">
                    <h4>Practice — Cities</h4>
                    <dl>
                      <div><dt>Rounds</dt><dd>{stats.by_format.practice_cities.rounds}</dd></div>
                      <div><dt>Median guess</dt><dd>{formatKm(stats.by_format.practice_cities.median_km)}</dd></div>
                      <div><dt>Best guess</dt><dd>{formatKm(stats.by_format.practice_cities.best_km)}</dd></div>
                    </dl>
                  </div>
                  <div class="stats__format-card">
                    <h4>Practice — Countries</h4>
                    <dl>
                      <div><dt>Rounds</dt><dd>{stats.by_format.practice_countries.rounds}</dd></div>
                      <div>
                        <dt>Best streak</dt>
                        <dd>
                          {#if stats.by_format.practice_countries.streak_best !== null}
                            🔥 {stats.by_format.practice_countries.streak_best}
                          {:else}
                            —
                          {/if}
                        </dd>
                      </div>
                    </dl>
                    {#if stats.by_format.practice_countries.per_filter.length > 0}
                      <ul class="stats__filter-list" aria-label="Best streak by region">
                        {#each stats.by_format.practice_countries.per_filter as f (f.mode_id + ':' + f.filter_slug)}
                          <li class="stats__filter-row">
                            <span class="stats__filter-name">{filterLabel(f.filter_slug)}</span>
                            <span class="stats__filter-streak">🔥 {f.streak_best}</span>
                          </li>
                        {/each}
                      </ul>
                    {/if}
                  </div>
                  <div class="stats__format-card">
                    <h4>Quiz</h4>
                    <dl>
                      <div><dt>Runs</dt><dd>{stats.by_format.quiz.runs}</dd></div>
                    </dl>
                    {#if stats.by_format.quiz.per_mode.length > 0}
                      <ul class="stats__quiz-list" aria-label="Best run by quiz mode">
                        {#each stats.by_format.quiz.per_mode as q (q.mode_id)}
                          <li class="stats__quiz-row">
                            <span class="stats__quiz-name">{quizModeLabel(q.mode_id)}</span>
                            <span class="stats__quiz-best">{formatQuizBest(q.best_run)}</span>
                          </li>
                        {/each}
                      </ul>
                    {/if}
                  </div>
                </div>
              </div>
            {:else}
              <p class="profile__section-body">
                No rounds played yet.
              </p>
            {/if}
          </section>

          {#if stats && stats.recent_dailies.length > 0}
            <section class="profile__section">
              <h3 class="profile__section-title">Recent dailies</h3>
              <RecentDailiesTable rows={stats.recent_dailies} />
            </section>
          {/if}
        {:else if activeTab === 'following'}
          <FollowsList username={profile.username} kind="following" />
        {:else if activeTab === 'followers'}
          <FollowsList username={profile.username} kind="followers" />
        {/if}
      </div>
    </div>
  </Card>
{/if}
